import { LiquidityOverlay, orientSegments, fitPriceRange } from "./LiquidityOverlay";
import { priceFrame } from "../lib/priceFrame";
import {
  CandlestickSeries,
  ColorType,
  createChart,
  LineStyle,
  type IChartApi,
  type ISeriesApi,
  type LogicalRange,
  type MouseEventParams,
} from "lightweight-charts";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { RefreshButton } from "./RefreshButton";
import { useRefreshPhase } from "../lib/useRefreshPhase";
import { formatChartPrice, formatChartTickmarks, formatPrice } from "../lib/format";
import type { ChartInterval, Pool, PoolChartResponse, PoolLiquidityResponse } from "../types";

const INTERVALS: ChartInterval[] = ["1m", "5m", "15m", "1h", "4h", "1d"];
const CHART_FONT_SIZE = 11;
const CHART_TEXT_RENDERING: CanvasTextRendering = "geometricPrecision";

function themeColor(name: string, fallback: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

function availableDocumentFonts(): FontFaceSet | null {
  const fonts: unknown = Reflect.get(document, "fonts");
  if (!fonts || typeof fonts !== "object") return null;
  const load: unknown = Reflect.get(fonts, "load");
  return typeof load === "function" ? (fonts as FontFaceSet) : null;
}

function createChartWithExactTextRendering(
  node: HTMLElement,
  options: Parameters<typeof createChart>[1],
): { chart: IChartApi; dispose: () => void } {
  const restoreGetContext = new Map<HTMLCanvasElement, PropertyDescriptor | undefined>();
  const prepareCanvas = (canvas: HTMLCanvasElement) => {
    if (restoreGetContext.has(canvas)) return;
    const ownDescriptor = Object.getOwnPropertyDescriptor(canvas, "getContext");
    const originalGetContext = Reflect.get(canvas, "getContext") as (
      contextId: string,
      ...args: unknown[]
    ) => RenderingContext | null;
    restoreGetContext.set(canvas, ownDescriptor);
    Object.defineProperty(canvas, "getContext", {
      configurable: true,
      writable: true,
      // Resizing a canvas bitmap resets its context state, so restore this on every access.
      value: ((contextId: string, ...args: unknown[]) => {
        const context = Reflect.apply(originalGetContext, canvas, [contextId, ...args]);
        if (contextId === "2d" && context) {
          (context as CanvasRenderingContext2D).textRendering = CHART_TEXT_RENDERING;
        }
        return context;
      }) as typeof canvas.getContext,
    });
  };
  const prepareTree = (element: Element) => {
    if (element instanceof HTMLCanvasElement) prepareCanvas(element);
    for (const canvas of element.querySelectorAll("canvas")) prepareCanvas(canvas);
  };
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const added of record.addedNodes) {
        if (added instanceof Element) prepareTree(added);
      }
    }
  });
  observer.observe(node, { childList: true, subtree: true });

  try {
    const chart = createChart(node, options);
    // The chart builds its canvas tree before returning and adds real series data afterward.
    prepareTree(node);
    return {
      chart,
      dispose: () => {
        observer.disconnect();
        for (const [canvas, descriptor] of restoreGetContext) {
          if (descriptor) Object.defineProperty(canvas, "getContext", descriptor);
          else Reflect.deleteProperty(canvas, "getContext");
        }
      },
    };
  } catch (error) {
    observer.disconnect();
    for (const [canvas, descriptor] of restoreGetContext) {
      if (descriptor) Object.defineProperty(canvas, "getContext", descriptor);
      else Reflect.deleteProperty(canvas, "getContext");
    }
    throw error;
  }
}

interface MarketChartProps {
  pool: Pool;
  referenceAt?: string;
  referenceSource?: "catalog snapshot" | "funding pool observation";
  quoteMint: string;
  current: string;
  lower?: string;
  upper?: string;
  lowerStop?: string;
  upperStop?: string;
}

function numeric(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function addLine(
  series: ISeriesApi<"Candlestick">,
  value: string | undefined,
  title: string,
  color: string,
  style = LineStyle.Solid,
  axisBackground = color,
  axisText = themeColor("--surface", "#ffffff"),
) {
  const price = numeric(value);
  if (price === null) return;
  series.createPriceLine({
    price,
    title,
    color,
    lineWidth: 1,
    lineStyle: style,
    axisLabelVisible: true,
    axisLabelColor: axisBackground,
    axisLabelTextColor: axisText,
  });
}

export function MarketChart(props: MarketChartProps) {
  const { pool, quoteMint, current, lower, upper, lowerStop, upperStop } = props;
  const [chartFontReady, setChartFontReady] = useState(() => availableDocumentFonts() === null);
  const [interval, setInterval] = useState<ChartInterval>("1d");
  const [refresh, setRefresh] = useState(0);
  const [chartData, setChartData] = useState<PoolChartResponse | null>(null);
  const [chartIdentity, setChartIdentity] = useState("");
  const [profile, setProfile] = useState<PoolLiquidityResponse | null>(null);
  const [profilePool, setProfilePool] = useState("");
  const [chartLoading, setChartLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [chartError, setChartError] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [liquidityTip, setLiquidityTip] = useState<string | null>(null);
  const [focusMode, setFocusMode] = useState<"fit" | "history" | "manual">("history");
  const [focusAction, setFocusAction] = useState(0);
  const overlay = useRef(new LiquidityOverlay());
  const container = useRef<HTMLDivElement | null>(null);
  const chart = useRef<IChartApi | null>(null);
  const series = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const lastViewportIdentity = useRef("");
  const lastVisibleRange = useRef<LogicalRange | null>(null);
  const lastVisiblePriceRange = useRef<{ from: number; to: number } | null>(null);
  const lastQuoteMint = useRef(quoteMint);
  const hasVisibleCandles = useRef(false);
  const desiredChartIdentity = `${pool.address}:${interval}:${quoteMint}`;
  const effectiveChartData = chartIdentity === desiredChartIdentity ? chartData : null;
  // A resolved response can still carry no fresh data: the server answers "unavailable" or a
  // cached chart when its provider fails. Neither is an update the pill may confirm.
  const refreshButton = useRefreshPhase({
    pending: chartLoading || profileLoading,
    failed:
      chartError !== null ||
      profileError !== null ||
      effectiveChartData === null ||
      effectiveChartData.status !== "available" ||
      effectiveChartData.stale ||
      profile?.status !== "available",
  });
  const effectiveProfile = profilePool === pool.address ? profile : null;

  useEffect(() => {
    const controller = new AbortController();
    const query = new URLSearchParams({ interval, quoteMint });
    setChartLoading(true);
    setChartError(null);
    api<PoolChartResponse>(`/pools/${pool.address}/chart?${query}`, {
      signal: controller.signal,
    }).then(
      (nextChart) => {
        if (!controller.signal.aborted) {
          setChartData(nextChart);
          setChartIdentity(desiredChartIdentity);
          setChartLoading(false);
        }
      },
      (reason: unknown) => {
        if (!controller.signal.aborted) {
          setChartError(reason instanceof Error ? reason.message : "Chart data is unavailable");
          setChartLoading(false);
        }
      },
    );
    return () => {
      controller.abort();
    };
  }, [desiredChartIdentity, interval, pool.address, quoteMint, refresh]);

  useEffect(() => {
    const controller = new AbortController();
    const query = new URLSearchParams({ quoteMint: pool.tokenB.mint });
    setProfileLoading(true);
    setProfileError(null);
    api<PoolLiquidityResponse>(`/pools/${pool.address}/liquidity?${query}`, {
      signal: controller.signal,
    }).then(
      (nextProfile) => {
        if (!controller.signal.aborted) {
          setProfile(nextProfile);
          setProfilePool(pool.address);
          setProfileLoading(false);
        }
      },
      (reason: unknown) => {
        if (!controller.signal.aborted) {
          setProfileError(
            reason instanceof Error ? reason.message : "Liquidity coverage is unavailable",
          );
          setProfileLoading(false);
        }
      },
    );
    return () => {
      controller.abort();
    };
  }, [pool.address, pool.tokenB.mint, refresh]);

  useEffect(() => {
    const fonts = availableDocumentFonts();
    if (!fonts) return;
    let active = true;
    const fontFamily = themeColor("--font-mono", '"Geist Mono", monospace');
    // lightweight-charts caches canvas text widths and cannot observe a webfont swap.
    void fonts.load(`${String(CHART_FONT_SIZE)}px ${fontFamily}`).then(
      () => {
        if (active) setChartFontReady(true);
      },
      () => {
        if (active) setChartFontReady(true);
      },
    );
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!chartFontReady) return;
    if (!container.current) return;
    const node = container.current;
    const surface = themeColor("--surface", "#ffffff");
    const faint = themeColor("--faint", "#8b9194");
    const line = themeColor("--line", "#efede7");
    const lineCard = themeColor("--line-card", "#e3e1da");
    const positive = themeColor("--pos-bar", "#19b588");
    const negative = themeColor("--neg-bar", "#e0685c");
    const exactRendering = createChartWithExactTextRendering(node, {
      height: node.clientHeight || 320,
      width: node.clientWidth,
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: surface },
        textColor: faint,
        fontFamily: themeColor("--font-mono", '"Geist Mono", monospace'),
        fontSize: CHART_FONT_SIZE,
        attributionLogo: false,
      },
      grid: { vertLines: { color: line }, horzLines: { color: line } },
      crosshair: { mode: 0 },
      timeScale: { timeVisible: true, secondsVisible: false, borderColor: lineCard },
      rightPriceScale: {
        alignLabels: true,
        borderColor: lineCard,
        scaleMargins: { top: 0.04, bottom: 0.04 },
      },
    });
    const nextChart = exactRendering.chart;
    const nextSeries = nextChart.addSeries(CandlestickSeries, {
      upColor: positive,
      downColor: negative,
      borderVisible: false,
      wickUpColor: positive,
      wickDownColor: negative,
      priceFormat: {
        type: "custom",
        formatter: formatChartPrice,
        tickmarksFormatter: formatChartTickmarks,
      },
    });
    const rememberVisibleRange = (range: LogicalRange | null) => {
      if (range) lastVisibleRange.current = range;
    };
    const primitive = overlay.current;
    nextSeries.attachPrimitive(primitive);
    const markManual = () => {
      if (!hasVisibleCandles.current) return;
      const range = nextSeries.priceScale().getVisibleRange();
      if (!range) return;
      lastVisiblePriceRange.current = range;
      nextSeries.priceScale().setVisibleRange(range);
      setFocusMode("manual");
    };
    const drag = (event: PointerEvent) => {
      if (event.buttons) markManual();
    };
    nextChart.timeScale().subscribeVisibleLogicalRangeChange(rememberVisibleRange);
    node.addEventListener("wheel", markManual);
    node.addEventListener("pointermove", drag);
    chart.current = nextChart;
    series.current = nextSeries;
    return () => {
      nextChart.timeScale().unsubscribeVisibleLogicalRangeChange(rememberVisibleRange);
      node.removeEventListener("wheel", markManual);
      node.removeEventListener("pointermove", drag);
      nextSeries.detachPrimitive(primitive);
      nextChart.remove();
      exactRendering.dispose();
      chart.current = null;
      series.current = null;
      lastViewportIdentity.current = "";
      lastVisibleRange.current = null;
      lastVisiblePriceRange.current = null;
      hasVisibleCandles.current = false;
    };
  }, [chartFontReady, pool.address]);

  useEffect(() => {
    const activeChart = chart.current;
    const activeSeries = series.current;
    if (!activeChart || !activeSeries || effectiveChartData?.status !== "available") return;
    const currentVisibleRange = activeChart.timeScale().getVisibleLogicalRange();
    if (currentVisibleRange) lastVisibleRange.current = currentVisibleRange;
    const currentPriceRange = activeSeries.priceScale().getVisibleRange();
    if (currentPriceRange) lastVisiblePriceRange.current = currentPriceRange;
    activeSeries.setData(
      effectiveChartData.candles.map((candle) => ({
        time: candle.time as never,
        open: Number(candle.open),
        high: Number(candle.high),
        low: Number(candle.low),
        close: Number(candle.close),
      })),
    );
    activeChart.timeScale().applyOptions({ timeVisible: interval !== "1d" });
    const viewportIdentity = `${pool.address}:${interval}`;
    const sameViewport = lastViewportIdentity.current === viewportIdentity;
    if (sameViewport && lastVisibleRange.current) {
      activeChart.timeScale().setVisibleLogicalRange(lastVisibleRange.current);
    } else {
      const width = container.current?.clientWidth ?? 600;
      const count = effectiveChartData.candles.length;
      const visibleBars = Math.max(count + 6, Math.ceil(width / 6));
      const padding = (visibleBars - count) / 2;
      activeChart.timeScale().applyOptions({ barSpacing: 6 });
      activeChart.timeScale().setVisibleLogicalRange({
        from: -padding,
        to: Math.max(0, count - 1) + padding,
      } as LogicalRange);
      activeSeries.priceScale().setAutoScale(true);
      setFocusMode("history");
    }
    if (sameViewport && focusMode === "manual" && lastVisiblePriceRange.current) {
      if (lastQuoteMint.current === quoteMint)
        activeSeries.priceScale().setVisibleRange(lastVisiblePriceRange.current);
      else activeSeries.priceScale().setAutoScale(true);
    }
    hasVisibleCandles.current = effectiveChartData.candles.length > 0;
    lastQuoteMint.current = quoteMint;
    lastViewportIdentity.current = viewportIdentity;
  }, [chartFontReady, effectiveChartData, focusMode, interval, pool.address, quoteMint]);

  useEffect(() => {
    const active = series.current;
    if (!active) return;
    const values = [current, lower, upper, lowerStop, upperStop]
      .map(numeric)
      .filter((value): value is number => value !== null)
      .sort((a, b) => a - b);
    const differences = values.slice(1).map((value, index) => value - (values[index] ?? value));
    const resolution = Math.min(...values, ...differences.filter((value) => value > 0));
    const movementExponent = Math.max(
      -300,
      Math.min(0, Math.floor(Math.log10(Number.isFinite(resolution) ? resolution : 1)) - 2),
    );
    active.applyOptions({
      priceFormat: {
        type: "custom",
        formatter: (value: number) => formatChartPrice(value, values),
        tickmarksFormatter: formatChartTickmarks,
        minMove: 10 ** movementExponent,
        base: 10 ** -movementExponent,
      },
    });
    active.priceLines().forEach((line) => {
      active.removePriceLine(line);
    });
    addLine(active, current, "Reference", themeColor("--text", "#15191b"), LineStyle.Dotted);
    const bound = themeColor("--pos-bar", "#19b588");
    const boundBackground = themeColor("--pos-bg", "#e2f4ec");
    const boundText = themeColor("--pos-text", "#0b7858");
    addLine(active, lower, "LP min", bound, LineStyle.Solid, boundBackground, boundText);
    addLine(active, upper, "LP max", bound, LineStyle.Solid, boundBackground, boundText);
    const stop = themeColor("--warn-dot", "#d6951e");
    addLine(
      active,
      lowerStop,
      "Lower stop",
      stop,
      LineStyle.Dashed,
      themeColor("--warn-chip", "#fbf1dc"),
      themeColor("--warn", "#9a6a12"),
    );
    addLine(
      active,
      upperStop,
      "Upper stop",
      stop,
      LineStyle.Dashed,
      themeColor("--warn-chip", "#fbf1dc"),
      themeColor("--warn", "#9a6a12"),
    );
  }, [chartFontReady, current, lower, upper, lowerStop, upperStop]);

  const oriented = useMemo(() => {
    if (effectiveProfile?.status !== "available") return [];
    if (quoteMint !== effectiveProfile.quoteMint && quoteMint !== effectiveProfile.baseMint)
      return [];
    try {
      return orientSegments(effectiveProfile.segments, quoteMint !== effectiveProfile.quoteMint);
    } catch {
      return [];
    }
  }, [effectiveProfile, quoteMint]);
  useEffect(() => {
    overlay.current.update(oriented, lower, upper);
  }, [oriented, lower, upper]);
  useEffect(() => {
    const activeChart = chart.current,
      activeSeries = series.current;
    if (!activeChart || !activeSeries) return;
    const hover = (event: MouseEventParams) => {
      const range = activeSeries.priceScale().getVisibleRange();
      const p = event.point ? activeSeries.coordinateToPrice(event.point.y) : null;
      const level =
        p === null
          ? undefined
          : oriented.find((s) => Number(s.lowerPrice) <= p && p < Number(s.upperPrice));
      if (p === null || !level || !range) {
        setLiquidityTip(null);
        return;
      }
      const max = oriented
        .filter((s) => Number(s.upperPrice) > range.from && Number(s.lowerPrice) < range.to)
        .reduce((m, s) => (BigInt(s.activeLiquidity) > m ? BigInt(s.activeLiquidity) : m), 0n);
      const ratio = max ? Number((BigInt(level.activeLiquidity) * 10_000n) / max) / 100 : 0;
      setLiquidityTip(`Price ${formatChartPrice(p)} · relative L ${String(ratio)}%`);
    };
    activeChart.subscribeCrosshairMove(hover);
    return () => {
      activeChart.unsubscribeCrosshairMove(hover);
    };
  }, [chartFontReady, oriented]);

  useEffect(() => {
    if (focusAction === 0) return;
    const scale = series.current?.priceScale();
    if (!scale) return;
    if (focusMode === "history") {
      chart.current?.timeScale().fitContent();
      const count =
        effectiveChartData?.status === "available" ? effectiveChartData.candles.length : 0;
      const width = container.current?.clientWidth ?? 600;
      if (count > 0 && count < Math.ceil(width / 6)) {
        const visibleBars = Math.ceil(width / 6);
        const padding = (visibleBars - count) / 2;
        chart.current?.timeScale().applyOptions({ barSpacing: 6 });
        chart.current?.timeScale().setVisibleLogicalRange({
          from: -padding,
          to: count - 1 + padding,
        } as LogicalRange);
      }
      scale.setAutoScale(true);
    } else if (focusMode === "fit") {
      const range = fitPriceRange([current, lower, upper, lowerStop, upperStop]);
      if (range) scale.setVisibleRange(range);
    }
  }, [
    chartFontReady,
    focusAction,
    focusMode,
    current,
    lower,
    upper,
    lowerStop,
    upperStop,
    effectiveChartData,
  ]);

  return (
    <section className="market-chart" aria-label="Pool price chart">
      <header className="chart-toolbar">
        <div className="segmented" aria-label="Chart interval">
          {INTERVALS.map((value) => (
            <button
              type="button"
              key={value}
              aria-pressed={interval === value}
              className={interval === value ? "active" : ""}
              onClick={() => {
                setInterval(value);
                setFocusMode("history");
              }}
            >
              {value}
            </button>
          ))}
        </div>
        <div className="segmented" aria-label="Price viewport">
          <button
            type="button"
            aria-pressed={focusMode === "fit"}
            className={focusMode === "fit" ? "active" : ""}
            onClick={() => {
              setFocusMode("fit");
              setFocusAction((value) => value + 1);
            }}
          >
            Range
          </button>
          <button
            type="button"
            aria-pressed={focusMode === "history"}
            className={focusMode === "history" ? "active" : ""}
            onClick={() => {
              setFocusMode("history");
              setFocusAction((value) => value + 1);
            }}
          >
            All
          </button>
          {focusMode === "manual" && <span>Manual</span>}
        </div>
        <div className="chart-status">
          <span title={chartError ?? undefined}>
            {chartLoading && effectiveChartData ? "Refreshing chart…" : null}
            {!chartLoading && chartError && effectiveChartData ? "Chart refresh failed" : null}
            {!chartLoading && !chartError
              ? effectiveChartData?.stale
                ? "Cached chart"
                : "GeckoTerminal OHLC"
              : null}
          </span>
          <RefreshButton
            phase={refreshButton.phase}
            ariaLabel="Refresh"
            title="Refresh chart"
            label="Refresh"
            onClick={() => {
              if (refreshButton.start()) setRefresh((value) => value + 1);
            }}
          />
        </div>
      </header>
      <div className="chart-layout">
        <div className="chart-price-pane">
          {chartLoading && !effectiveChartData && (
            <div className="chart-message">Loading chart data…</div>
          )}
          {!chartLoading && effectiveChartData?.status !== "available" && (
            <div className="chart-message">
              {chartError ?? effectiveChartData?.reason ?? "Chart data is unavailable"}
            </div>
          )}
          <div className="chart-canvas" ref={container} data-testid="lightweight-chart" />
          <a
            href="https://www.tradingview.com/"
            target="_blank"
            rel="noreferrer"
            className="chart-attribution"
          >
            Charts by TradingView
          </a>
        </div>
      </div>
      <aside className="liquidity-legend" aria-label="On-chain active liquidity profile">
        <span aria-live="off">
          {liquidityTip ?? "Hover a covered price to inspect relative liquidity"}
        </span>
        <span>
          Gray / green: relative active liquidity ·{" "}
          {priceFrame(pool.tokenA, pool.tokenB, quoteMint === pool.tokenA.mint).unit}
        </span>
        <span>
          Reference {formatPrice(current)} · {props.referenceSource ?? "catalog snapshot"}{" "}
          {props.referenceAt ? new Date(props.referenceAt).toLocaleString() : "time unavailable"}.
          Latest candle is a separate pool trade observation.
        </span>
        <span role={profileError ? "alert" : undefined}>
          {profileError
            ? "Liquidity refresh failed. Last loaded liquidity is shown. " + profileError
            : profileLoading
              ? "Loading active liquidity…"
              : effectiveProfile?.status === "available"
                ? effectiveProfile.coverage.complete
                  ? "Full initialized range covered"
                  : "Partial coverage · outside validated arrays is unknown"
                : (effectiveProfile?.reason ?? "Liquidity coverage is unavailable")}
        </span>
      </aside>
    </section>
  );
}
